import React, { useState, useEffect } from 'react'

import { SearchIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react'

import {
  MenuItem,
  TextField,
  Autocomplete as MuiAutocomplete,
  AutocompleteProps as MuiAutocompleteProps,
  CircularProgress,
  InputAdornment,
  Stack,
} from '@mui/material'
import { styled, useTheme } from '@mui/material/styles'

import {
  fieldAccentColor,
  fieldBorderColor,
  fieldFill,
  fieldFocusRing,
  FIELD_FONT_SIZE,
  FIELD_PLACEHOLDER_OPACITY,
  FIELD_RADIUS,
  TOOLBAR_CONTROL_HEIGHT,
} from '../../../theme/fieldChrome'

const StyledMuiAutocomplete = styled(MuiAutocomplete)(({ theme }) => ({
  width: '100%',
  // borderRadius: 8,
  // fontSize: 14,
  // lineHeight: 1.5,
  // pb: 4,
  // '.MuiAutocomplete-standardWarning': {
  //   borderColor: theme.palette.grey[300]
  // },
  // ...theme.applyStyles('dark', {
  //   backgroundColor: theme.palette.secondary.main,
  //   '&:hover': {
  //     backgroundColor: theme.palette.secondary.dark,
  //   },
  // })
}))

interface AutocompleteProps {
  // Core functionality
  value?: any
  onClose?: () => void
  onChange?: (value: any) => void
  onSelect?: (value: any) => void
  options?: any[]
  
  // MUI Autocomplete props
  autoHighlight?: boolean
  getOptionLabel?: (option: any) => string
  isOptionEqualToValue?: (option: any, value: any) => boolean
  /** Mark options that exist but cannot be chosen — shown greyed and unselectable, so the
   *  reason can be rendered beside them rather than the option silently vanishing. */
  getOptionDisabled?: (option: any) => boolean
  renderInput?: (params: any) => React.ReactNode
  renderOption?: (option: any) => React.ReactNode
  disablePortal?: boolean
  // Multi-select support (opt-in; single-select behavior is unchanged when false).
  multiple?: boolean
  renderTags?: (value: any[], getTagProps: any) => React.ReactNode
  groupBy?: (option: any) => string
  disableCloseOnSelect?: boolean
  // Server-side search support: forward the typed value + skip client filtering.
  onInputChange?: (event: any, value: string, reason: string) => void
  filterOptions?: (options: any[], state: any) => any[]
  loading?: boolean
  noOptionsText?: React.ReactNode
  
  // Styling
  sx?: any
  size?: 'small' | 'medium'
  fullWidth?: boolean
  // Hide the leading search icon — for labeled filter fields where the label already
  // identifies the filter, so the affordance glyph is redundant (cleaner, iconless).
  hideStartIcon?: boolean
  
  // Form props (React Hook Form compatible)
  name?: string
  placeholder?: string
  disabled?: boolean
  error?: boolean
  helperText?: string
  required?: boolean
  
  // Additional props for form integration
  inputRef?: React.RefObject<HTMLInputElement>
  onBlur?: () => void
  onFocus?: () => void
}

export const Autocomplete = ({
  value,
  onClose,
  onChange,
  renderOption,
  onSelect,
  options = [],
  autoHighlight = true,
  getOptionLabel,
  isOptionEqualToValue,
  getOptionDisabled,
  renderInput,
  sx = {},
  disablePortal = false,
  placeholder = 'Search',
  name,
  disabled = false,
  error = false,
  helperText,
  required = false,
  size = 'medium',
  fullWidth = true, // Default to full width
  hideStartIcon = false,
  inputRef,
  onBlur,
  onFocus,
  onInputChange,
  filterOptions,
  loading = false,
  noOptionsText,
  multiple = false,
  renderTags,
  groupBy,
  disableCloseOnSelect,
}: AutocompleteProps) => {
  const theme = useTheme()
  const [internalOptions, setInternalOptions] = useState(options)
  const [open, setOpen] = useState(false)

  // Update internal options when options prop changes
  useEffect(() => {
    setInternalOptions(options)
  }, [options])

  const handleOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    if (onClose) {
      onClose()
    }
  }

  const handleChange = (event: any, newValue: any) => {
    if (onChange) {
      onChange(newValue)
    }
    if (onSelect) {
      onSelect(newValue)
    }
  }

  const handleOptionSelect = (option: any) => {
    if (onSelect) {
      onSelect(option)
    }
    if (onChange) {
      onChange(option)
    }
    // Close the dropdown after selection
    handleClose()
  }

  // Default option label function
  const defaultGetOptionLabel = (option: any): string => {
    if (!option) return ''
    if (typeof option === 'string') return option
    if (typeof option === 'object' && option.title) return option.title
    if (typeof option === 'object' && option.label) return option.label
    if (typeof option === 'object' && option.name) return option.name
    return String(option)
  }

  // Default option equality function
  const defaultIsOptionEqualToValue = (option: any, value: any): boolean => {
    if (!option || !value) return false
    if (option.id && value.id) return option.id === value.id
    if (option.title && value.title) return option.title === value.title
    if (option.label && value.label) return option.label === value.label
    return option === value
  }

  return (
    <StyledMuiAutocomplete
      id={name || "autocomplete"}
      value={value}
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      onChange={handleChange}
      onInputChange={onInputChange}
      filterOptions={filterOptions}
      noOptionsText={noOptionsText}
      multiple={multiple}
      renderTags={renderTags}
      groupBy={groupBy}
      disableCloseOnSelect={disableCloseOnSelect ?? multiple}
      autoComplete={true}
      loading={loading}
      disablePortal={disablePortal}
      options={internalOptions}
      getOptionLabel={getOptionLabel || defaultGetOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue || defaultIsOptionEqualToValue}
      autoHighlight={autoHighlight}
      disabled={disabled}
      fullWidth={fullWidth}
      size={size}
      sx={{
        width: fullWidth ? '100%' : 'auto',
        paddingTop:  '0px !important',
        ...sx
      }}
      renderInput={renderInput || ((params: any) => (
        <TextField
          {...params}
          variant="filled"
          placeholder={placeholder}
          name={name}
          disabled={disabled}
          error={error}
          helperText={helperText}
          required={required}
          size={size}
          fullWidth={fullWidth}
          inputRef={inputRef}
          onBlur={onBlur}
          onFocus={onFocus}
          sx={{ paddingTop:  '0px !important'}}
          InputProps={{
            ...params.InputProps,
            disableUnderline: true,
            startAdornment: hideStartIcon ? (
              params.InputProps.startAdornment
            ) : (
              <Stack direction="row" alignItems="center" spacing={2} pl={1.5}>
                <SearchIcon strokeWidth={1} size={18} />
                {params.InputProps.startAdornment}
              </Stack>
            ),
            endAdornment: (
              // Flush-right chevron (no default InputAdornment left-margin so it doesn't
              // sit indented from the field's right edge).
              <InputAdornment position="end" sx={{ ml: 0, mr: 0 }}>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {open ? <ChevronUpIcon strokeWidth={1} size={20} /> : <ChevronDownIcon strokeWidth={1} size={20} />}
              </InputAdornment>
            ),
            sx: {
              paddingTop: '0px !important',
              alignItems: 'center',
              /*
                FIELD CHROME COMES FROM `fieldChrome`, NOT FROM LITERALS (§31/§47).

                Every value below used to be re-derived here, and three of the four had drifted
                from the field beside them. Measured in the analytics Filters drawer, dark mode,
                where this component (Location) sits directly under three `@repo/ui` `Input`s
                (Owner / Product / Certificate):

                  fill    rgb(24,24,27)  vs the siblings' rgb(31,31,35)
                  height  47             vs 49
                  hover   brand[500]     vs brand[600]
                  focus   brand[500],    vs brand[600] + the soft ring
                          NO ring

                Only the border colour agreed, and only by coincidence — it was written as
                `primary[100]`, which is what `fieldBorderColor` happens to resolve to.

                The height is the instructive one. Its old comment read "match the other filter
                fields' height (was ~39px vs their 47px)" — i.e. someone had already tried to
                match by eye and landed 2px short, and the number then froze while the shared
                token moved. That is precisely the failure a hardcoded copy of a shared value
                produces, and why these are helper calls now: `CONTROL_HEIGHT` is the page-header
                band (40) and `TOOLBAR_CONTROL_HEIGHT` is the field band (49) — a field takes the
                latter, and takes it by name so it cannot drift again.
              */
              minHeight: TOOLBAR_CONTROL_HEIGHT,
              /*
                TYPOGRAPHY COMES FROM `fieldChrome` TOO, AND IT GOES ON THE ROOT.

                The chrome pass above fixed fill, border, height, radius and focus and left the
                TEXT alone, on the reasonable-looking evidence that the font-size already agreed.
                It did — on the inner `<input>`, which `inputProps.sx` set to 16 — and nowhere
                else. Measured in the analytics Filters drawer, light mode, against the three
                `Input`-backed search fields directly above this one:

                  input font-size        16px  vs 16px   ← the property that was checked
                  ROOT font-size         16px  vs 20px
                  line-height            23px  vs 28.75px
                  placeholder opacity    0.65  vs 1

                20px is `typography.body1`, which MUI's InputBase spreads onto the root; MUI's
                `line-height: 1.4375em` is declared on the root as well, so it resolved against
                20 and inherited down as a length. And the placeholder, at the SAME 16px, SAME
                Lato, SAME weight 400 and SAME `rgba(0,0,0,0.42)`, rendered at effective alpha
                0.42 against the siblings' 0.273 — 1.54× the contrast, which is what reads as a
                different, heavier font when nothing about the font differs.

                Both now come from the shared tokens rather than a literal copied from `Input`,
                since a literal that has to agree with another file's literal is the same defect
                one property along. `inputProps` no longer restates the size: the root owns it,
                exactly as it does in `Input`.
              */
              fontSize: FIELD_FONT_SIZE,
              '& .MuiInputBase-input::placeholder': {
                opacity: FIELD_PLACEHOLDER_OPACITY,
              },
              // Absolute px via FIELD_RADIUS, not the theme-relative `2` this was. A layer-1
              // component renders under whichever app's theme mounts it, and the two apps have
              // different spacing bases — `2` is 8px here and 16px in `apps/app` (§47).
              borderRadius: `${FIELD_RADIUS}px`,
              backgroundColor: fieldFill(theme),
              border: `1px solid ${fieldBorderColor(theme)}`,
              paddingLeft: theme.spacing(1.5),
              // MUI Autocomplete forces padding-right:39px on the input to reserve space
              // for its default indicator; our custom chevron is a flex child, so override
              // it to keep the chevron flush-right (was indented ~40px).
              paddingRight: `${theme.spacing(1.5)} !important`,
              transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
              /*
                DOUBLE-AMPERSAND, AND IT IS LOAD-BEARING — MEASURED, NOT PRECAUTIONARY.

                This was written as a single `&` first and the focus state silently did
                nothing. With the field genuinely focused (real click, `document.activeElement`
                confirmed, `Mui-focused` present on the root) it computed:

                  border  rgb(39,39,42)             — the REST border, unchanged
                  shadow  rgba(0,0,0,0) 0 0 0 0     — no ring at all
                  bg      rgba(255,255,255,0.09)    — MUI's own focus overlay, not `fieldFill`

                Because `sx` on `InputProps` emits ONE emotion class — `.css-hash`, specificity
                (0,1,0) — while MUI's FilledInput ships `.MuiFilledInput-root.Mui-focused` at
                (0,2,0). Later emission does not rescue a lower specificity, so MUI won every
                property in this block. At REST the same sx wins fine (MUI's resting rule is
                also (0,1,0) and we are emitted after), which is exactly why the fill, border,
                height and radius above all landed while this block quietly did not — a
                half-applied fix that looks applied.

                `&&` doubles the class to (0,2,0) and, emitted later, takes the tie. The idiom
                is already used further down this file to beat MenuItem's built-in min-height.

                The BACKGROUND has to be re-asserted here for the same reason `Input` re-asserts
                it (see its note): MUI's focused overlay is a white alpha, so without this line
                the field lightens on focus and its colour depends on whatever is behind it.
              */
              '&&:hover': {
                borderColor: fieldAccentColor(theme),
                backgroundColor: fieldFill(theme),
              },
              // `:focus-within` alongside `.Mui-focused` for the same reason `Input` carries
              // both: the root is a <div>, so a bare `:focus` never matches it.
              '&&.Mui-focused, &&:focus-within': {
                borderColor: fieldAccentColor(theme),
                boxShadow: fieldFocusRing(theme),
                backgroundColor: fieldFill(theme),
              },
            },
          }}
            inputProps={{
              ...params.inputProps,
              autoComplete: 'off',
              sx: {
                pl: 1,
                pr: 1,
                py: 2,
                pt: 0,
                paddingTop: 0,
                // No `fontSize` here — the root carries it (see the note in `InputProps.sx`).
                // Restating it on the input is what let the root keep body1's 20px unnoticed.
              },
            }}
        />
      ))}
      slotProps={{
        // Dropdown paper: gap below the input + rounded + subtle border + soft shadow
        // (matches other menus). The 'No options' text is sized to match the field text.
        paper: {
          sx: {
            // 8px — the house anchor→menu gap, NOT 4. This was `mt: 1`, so every consumer
            // of the shared Autocomplete — 14 of them, including the Add-packages variant
            // picker — opened its menu at HALF the gap of every other dropdown in the app
            // and read as flush against its own field. Measured 4.5px here against Sort's
            // 8.0px.
            //
            // Absolute px, not the `mt: 2` this briefly was: layer-1 components render under
            // whichever app's theme mounts them, and `2` is 8px on the @repo/ui 4px base but
            // 16px on the app's MUI default (§47). The canonical value now lives in
            // MENU_PAPER_SX; an Autocomplete popper is not a Menu paper, so it is restated
            // here rather than spread.
            mt: '8px',
            borderRadius: 2,
            // Kill MUI's dark-mode elevation overlay (a white linear-gradient that lightened
            // the paper). The base backgroundColor already matches the SearchSelect filter
            // popover (rgb(15,15,18)) — only the overlay was making it read lighter.
            backgroundImage: 'none',
            border: `1px solid ${theme.palette.primary?.[100] || theme.palette.divider}`,
            boxShadow: `rgba(145, 158, 171, 0.12) 0px 12px 24px -4px, rgba(145, 158, 171, 0.2) 0px 0px 2px 0px`,
            '& .MuiAutocomplete-noOptions': {
              fontSize: 14,
              py: 3,
              px: 4,
              color: theme.palette.text.secondary,
            },
          },
        },
      }}
      getOptionDisabled={getOptionDisabled}
      renderOption={(props: any, option: any) => {
        const { key: _ignoredKey, ...optionProps } = props
        const uniqueKey = `option-${option?.id ?? option?.sku ?? defaultGetOptionLabel(option)}`
        return (
          <MenuItem
            key={uniqueKey}
            {...optionProps}
            // Single-select closes on pick; multi-select keeps the menu's default
            // toggle-and-stay-open behavior (don't override its onClick).
            onClick={multiple ? optionProps.onClick : () => handleOptionSelect(option)}
            sx={{
              m: 2,
              mb: 1,
              px: 3,
              borderRadius: 2,
              justifyContent: 'space-between',
              // Double-class (&&) to beat MUI MenuItem's built-in min-height/padding and
              // the Autocomplete option's grey `.Mui-focused` override (single-class sx
              // only tied those, so it lost). Taller rows (48) = parity with SearchSelect.
              '&&': {
                minHeight: 48,
                paddingTop: '10px',
                paddingBottom: '10px',
              },
              // Brand-purple hover + keyboard-highlight (brand[600] #7F48DF at 12%),
              // matching the SearchSelect filter dropdown — replaces MUI's grey
              // action.hover, which lightened the row instead of tinting it darker.
              '&&:hover, &&.Mui-focused, &&.Mui-focusVisible': {
                backgroundColor: 'rgba(127, 72, 223, 0.12)',
              },
            }}
          >
            {renderOption ? renderOption(option) : defaultGetOptionLabel(option)}
          </MenuItem>
        )
      }}
    />
  )
}
